import * as THREE from 'three';
import { MeshBasicNodeMaterial } from 'three/webgpu';
import { uniform, positionLocal, normalize, clamp, mix, pow, max, dot, select } from 'three/tsl';
import { sunDirection } from '../../shared/sun.js';
import { skyState } from '../../shared/skycolours.js';

const SHADOW_RANGE = 130; // metres of shadow around the player when no setting says otherwise

// Sun, sky dome, haze and shadows, all driven by the real time and weather.
// nodes: true draws the dome with a node material (the new renderer), false with GLSL (legacy).
export class Sky {
  // shadows: { range, mapSize } — how far from the player shadows are drawn and at what resolution
  constructor(scene, region, nodes = false, shadows = {}) {
    this.scene = scene;
    this.region = region;
    this.weather = { cloud: 0.25, rain: 0, fog: 0 };

    this.hemi = new THREE.HemisphereLight(0xbcd7ff, 0x8a7a60, 1);
    scene.add(this.hemi);
    this.sun = new THREE.DirectionalLight(0xffffff, 3);
    this.sun.castShadow = true;
    const range = shadows.range || SHADOW_RANGE;
    const mapSize = shadows.mapSize || 2048;
    this.sun.shadow.mapSize.set(mapSize, mapSize);
    const cam = this.sun.shadow.camera;
    cam.left = -range;
    cam.right = range;
    cam.top = range;
    cam.bottom = -range;
    cam.near = 10;
    cam.far = 900;
    // the bias scales with the metres each shadow texel covers, so bigger maps keep contact shadows tight
    const texel = (range * 2) / mapSize;
    this.sun.shadow.bias = -0.0006;
    this.sun.shadow.normalBias = Math.max(0.25, texel * 4.7);
    scene.add(this.sun, this.sun.target);

    // the same Color and Vector objects feed both kinds of dome; they are changed in place
    this.colours = { top: new THREE.Color(), horizon: new THREE.Color(), sun: new THREE.Color(), hemi: new THREE.Color(), ground: new THREE.Color() };
    this.sunDir = new THREE.Vector3(0, 1, 0);
    this.glow = { value: 1 };
    const dome = new THREE.Mesh(new THREE.SphereGeometry(1, 32, 16), nodes ? this.nodeDome() : this.glslDome());
    dome.scale.setScalar(20000);
    dome.frustumCulled = false;
    dome.renderOrder = -1;
    this.dome = dome;
    scene.add(dome);

    scene.fog = new THREE.Fog(0xb9d6ee, 300, 6000);
    this.dir = new THREE.Vector3(0, 1, 0);
    this.daylight = 1;
  }

  // The dome for the legacy WebGLRenderer.
  glslDome() {
    this.uniforms = {
      uTop: { value: this.colours.top },
      uHorizon: { value: this.colours.horizon },
      uSunColor: { value: this.colours.sun },
      uSunDir: { value: this.sunDir },
      uGlow: this.glow,
    };
    return new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      side: THREE.BackSide,
      depthWrite: false,
      fog: false,
      vertexShader: 'varying vec3 vDir;\nvoid main() {\n  vDir = normalize(position);\n  vec4 p = modelViewMatrix * vec4(position, 1.0);\n  gl_Position = projectionMatrix * p;\n  gl_Position.z = gl_Position.w;\n}',
      fragmentShader:
        'uniform vec3 uTop;\nuniform vec3 uHorizon;\nuniform vec3 uSunColor;\nuniform vec3 uSunDir;\nuniform float uGlow;\nvarying vec3 vDir;\nvoid main() {\n  vec3 d = normalize(vDir);\n  float h = clamp(d.y, 0.0, 1.0);\n  vec3 col = mix(uHorizon, uTop, pow(h, 0.55));\n  float s = max(dot(d, normalize(uSunDir)), 0.0);\n  col += uSunColor * (pow(s, 900.0) * 6.0 + pow(s, 24.0) * 0.35 + pow(s, 4.0) * 0.12) * uGlow;\n  if (d.y < 0.0) col = mix(uHorizon, uHorizon * 0.7, clamp(-d.y * 4.0, 0.0, 1.0));\n  gl_FragColor = vec4(col, 1.0);\n  #include <tonemapping_fragment>\n  #include <colorspace_fragment>\n}',
    });
  }

  // The same dome as a node material, for the new renderer on either backend.
  nodeDome() {
    const top = uniform(this.colours.top);
    const horizon = uniform(this.colours.horizon);
    const sunColor = uniform(this.colours.sun);
    const sunDir = uniform(this.sunDir);
    const glow = uniform(1);
    this.glow = glow; // the node's value is what update() writes
    const d = normalize(positionLocal);
    const h = clamp(d.y, 0, 1);
    const above = mix(horizon, top, pow(h, 0.55));
    const s = max(dot(d, normalize(sunDir)), 0);
    const halo = pow(s, 900).mul(6).add(pow(s, 24).mul(0.35)).add(pow(s, 4).mul(0.12));
    const sky = above.add(sunColor.mul(halo).mul(glow));
    const below = mix(horizon, horizon.mul(0.7), clamp(d.y.negate().mul(4), 0, 1));
    const material = new MeshBasicNodeMaterial({ side: THREE.BackSide, depthWrite: false, fog: false });
    material.colorNode = select(d.y.lessThan(0), below, sky);
    return material;
  }

  setWeather(w) {
    Object.assign(this.weather, w);
  }

  // date: the moment to show. focus: the point shadows are centred on.
  update(date, focus, viewDistance) {
    const d = sunDirection(date, this.region.lat0, this.region.lon0);
    const alt = Math.asin(d[1]) / (Math.PI / 180); // degrees above the horizon
    const s = skyState(alt, this.weather, this.colours);
    this.glow.value = s.glow;

    this.dir.set(d[0], d[1], d[2]);
    if (!s.up) this.dir.set(-d[0], Math.max(0.35, -d[1]), -d[2]);
    if (this.dir.y < 0.08) this.dir.y = 0.08;
    this.dir.normalize();
    this.sunDir.set(d[0], d[1], d[2]);

    this.sun.intensity = s.direct;
    this.sun.color.copy(s.up ? this.colours.sun : s.moonColor);
    this.sun.position.set(focus.x + this.dir.x * 400, focus.y + this.dir.y * 400, focus.z + this.dir.z * 400);
    this.sun.target.position.copy(focus);
    this.sun.castShadow = s.direct > 0.4;

    this.hemi.intensity = s.ambient;
    this.hemi.color.copy(this.colours.hemi);
    this.hemi.groundColor.copy(this.colours.ground);

    this.scene.fog.color.copy(this.colours.horizon);
    this.scene.fog.near = viewDistance * 0.3 + (15 - viewDistance * 0.3) * s.haze;
    this.scene.fog.far = viewDistance * 4.2 + (220 - viewDistance * 4.2) * s.haze;
    this.dome.position.copy(focus);
    this.daylight = s.daylight;
  }
}
